/**
 * <CurrencyInput> — input that displays a value in ZAR with thousand-separators
 * and stores the underlying number as a JS number (not a string).
 *
 * Why this exists
 * ---------------
 * The buyer-portal calculator currently does ad-hoc formatting in every
 * page. Each page parses + reformats slightly differently, and at least one
 * page has a bug where pasting "R 1 250 000" produces NaN. A single
 * primitive kills the duplication and the bug.
 *
 * Strategy
 * --------
 * - Internal state: a string (so we can show "1 250 000" while the user
 *   types instead of round-tripping through Number every keystroke).
 * - External value: a number (or null if the input is empty).
 * - Locale: en-ZA. The R prefix and the space thousand-separator come
 *   from `Intl.NumberFormat`.
 */

import React, { forwardRef, useEffect, useState } from 'react';

const formatter = new Intl.NumberFormat('en-ZA', {
  style: 'decimal',
  maximumFractionDigits: 0,
});

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return formatter.format(parsed);
  }
  return formatter.format(value);
}

function parseNumber(displayValue) {
  if (!displayValue) return null;
  // Strip everything except digits.
  const digits = displayValue.replace(/[^\d]/g, '');
  if (digits === '') return null;
  return Number(digits);
}

/**
 * @typedef {object} CurrencyInputProps
 * @property {number | null} value
 * @property {(value: number | null) => void} onChange
 * @property {string} [id]
 * @property {string} [name]
 * @property {string} [placeholder]
 * @property {string} [className]
 * @property {string} [prefix]   — Default 'R'.
 * @property {number} [min]
 * @property {number} [max]
 */

export const CurrencyInput = forwardRef(function CurrencyInput(
  {
    value,
    onChange,
    id,
    name = 'amount',
    placeholder = '0',
    className = '',
    prefix = 'R',
    min,
    max,
    ...rest
  },
  ref,
) {
  // Local string state for typing UX. Sync from prop changes (e.g. external reset).
  const [display, setDisplay] = useState(formatNumber(value));

  useEffect(() => {
    setDisplay(formatNumber(value));
  }, [value]);

  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
      >
        {prefix}
      </span>
      <input
        ref={ref}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) => {
          const parsed = parseNumber(e.target.value);
          if (parsed !== null) {
            if (min !== undefined && parsed < min) return;
            if (max !== undefined && parsed > max) return;
          }
          // Reformat as the user types so the cursor doesn't lag.
          setDisplay(formatNumber(parsed));
          onChange(parsed);
        }}
        placeholder={placeholder}
        className={`w-full min-h-touch pl-8 pr-3 py-2 border border-slate-300 rounded-md text-base text-right tabular-nums ${className}`}
        {...rest}
      />
    </div>
  );
});
